import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { TourChapter, TourStep } from './chapters'
import styles from './Tour.module.css'

const TOOLTIP_GAP = 16
const SPOTLIGHT_PAD = 8
const TARGET_POLL_MS = 140
const TARGET_TIMEOUT_MS = 6000
const FORCE_ACK_MIN_MS = 4000

interface ChapterPlayerProps {
  chapter: TourChapter
  stepIndex: number
  onAdvance: () => void
  onCompleteChapter: () => void
  onPause: () => void
  onPickAnother: () => void
}

export function ChapterPlayer({
  chapter,
  stepIndex,
  onAdvance,
  onCompleteChapter,
  onPause,
  onPickAnother,
}: ChapterPlayerProps) {
  const step = chapter.steps[stepIndex]
  const navigate = useNavigate()
  const location = useLocation()

  const [rect, setRect] = useState<DOMRect | null>(null)
  const [readingDone, setReadingDone] = useState(false)
  const [observed, setObserved] = useState(false)

  const isLast = stepIndex === chapter.steps.length - 1
  const onPath = !step.path || location.pathname === step.path

  // ── Navigate to the step's path when the step starts ──────────────
  useEffect(() => {
    if (step.path && location.pathname !== step.path) {
      navigate(step.path)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id])

  // ── Reading-paced enablement (4s minimum for forceAck) ────────────
  const readingMs = step.forceAck
    ? Math.max(FORCE_ACK_MIN_MS, Math.round(step.body.length * 28))
    : Math.min(6000, Math.max(2500, Math.round(step.body.length * 28)))

  useEffect(() => {
    setReadingDone(false)
    setObserved(false)
    const t = setTimeout(() => setReadingDone(true), readingMs)
    return () => clearTimeout(t)
  }, [step.id, readingMs])

  // ── Poll for the target element ──────────────────────────────────
  useEffect(() => {
    if (!step.target || !onPath) { setRect(null); return }
    let mounted = true
    const start = Date.now()
    const tick = () => {
      if (!mounted) return
      const el = document.querySelector(step.target!) as HTMLElement | null
      if (el) { setRect(el.getBoundingClientRect()); return }
      if (Date.now() - start > TARGET_TIMEOUT_MS) return
      setTimeout(tick, TARGET_POLL_MS)
    }
    tick()
    return () => { mounted = false }
  }, [step.id, step.target, onPath])

  // ── Re-measure on resize/scroll + periodic for late-mount targets ─
  useEffect(() => {
    if (!step.target || !onPath) return
    const measure = () => {
      const el = document.querySelector(step.target!) as HTMLElement | null
      if (el) setRect(el.getBoundingClientRect())
      else setRect(null)
    }
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    const interval = window.setInterval(measure, 800)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
      window.clearInterval(interval)
    }
  }, [step.id, step.target, onPath])

  // ── Observe target event (soft auto-advance) ──────────────────────
  const observeEvent = step.observe?.event
  const observeCount = step.observe?.count ?? 1
  const observeProgressRef = useRef(0)

  useEffect(() => {
    observeProgressRef.current = 0
    if (!observeEvent || !step.target || !onPath) return
    const el = document.querySelector(step.target) as HTMLElement | null
    if (!el) return
    const handler = () => {
      observeProgressRef.current += 1
      if (observeProgressRef.current >= observeCount) setObserved(true)
    }
    el.addEventListener(observeEvent, handler)
    return () => el.removeEventListener(observeEvent, handler)
  }, [step.id, step.target, observeEvent, observeCount, onPath, rect])

  useEffect(() => {
    if (!observed) return
    // ForceAck never auto-advances on observation — user must explicitly ack
    if (step.forceAck) return
    const t = setTimeout(() => {
      if (isLast) onCompleteChapter()
      else onAdvance()
    }, 900)
    return () => clearTimeout(t)
  }, [observed, isLast, onAdvance, onCompleteChapter, step.forceAck])

  const handleGotIt = useCallback(() => {
    if (!readingDone) return
    if (isLast) onCompleteChapter()
    else onAdvance()
  }, [readingDone, isLast, onAdvance, onCompleteChapter])

  if (!onPath) return null

  return (
    <>
      {step.target && rect && (
        <div
          className={styles.spotlight}
          style={{
            left:   Math.max(0, rect.left   - SPOTLIGHT_PAD),
            top:    Math.max(0, rect.top    - SPOTLIGHT_PAD),
            width:  rect.width  + SPOTLIGHT_PAD * 2,
            height: rect.height + SPOTLIGHT_PAD * 2,
          }}
        />
      )}

      <StepTooltip
        chapter={chapter}
        step={step}
        stepIndex={stepIndex}
        totalSteps={chapter.steps.length}
        rect={rect}
        readingDone={readingDone}
        readingMs={readingMs}
        observed={observed}
        onGotIt={handleGotIt}
        onPause={onPause}
        onPickAnother={onPickAnother}
        isLast={isLast}
      />
    </>
  )
}

// ─── Tooltip ────────────────────────────────────────────────────────

interface StepTooltipProps {
  chapter: TourChapter
  step: TourStep
  stepIndex: number
  totalSteps: number
  rect: DOMRect | null
  readingDone: boolean
  readingMs: number
  observed: boolean
  isLast: boolean
  onGotIt: () => void
  onPause: () => void
  onPickAnother: () => void
}

function StepTooltip({
  chapter, step, stepIndex, totalSteps,
  rect, readingDone, readingMs, observed, isLast,
  onGotIt, onPause, onPickAnother,
}: StepTooltipProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const w = el.offsetWidth
    const h = el.offsetHeight
    const vw = window.innerWidth
    const vh = window.innerHeight

    if (!rect) {
      setPos({ top: Math.max(24, (vh - h) / 2), left: Math.max(16, (vw - w) / 2) })
      return
    }

    const preferred = step.placement ?? 'bottom'
    const order = Array.from(new Set([preferred, 'bottom', 'top', 'right', 'left'])) as Array<'bottom'|'top'|'right'|'left'>
    let best: { top: number; left: number } | null = null
    for (const p of order) {
      let t = 0, l = 0
      if (p === 'bottom')     { t = rect.bottom + TOOLTIP_GAP; l = rect.left + rect.width/2 - w/2 }
      else if (p === 'top')   { t = rect.top - TOOLTIP_GAP - h; l = rect.left + rect.width/2 - w/2 }
      else if (p === 'right') { l = rect.right + TOOLTIP_GAP; t = rect.top + rect.height/2 - h/2 }
      else                    { l = rect.left - TOOLTIP_GAP - w; t = rect.top + rect.height/2 - h/2 }
      if (t >= 12 && t + h <= vh - 12 && l >= 12 && l + w <= vw - 12) {
        best = { top: t, left: l }
        break
      }
      if (!best) best = { top: t, left: l }
    }
    if (best) {
      best.top  = Math.max(12, Math.min(vh - h - 12, best.top))
      best.left = Math.max(12, Math.min(vw - w - 12, best.left))
      setPos(best)
    }
  }, [rect, step.placement, step.id])

  const hint = step.observe?.hint
  const isFloating = !step.target
  const isAck = !!step.forceAck

  const buttonLabel = observed
    ? 'Done'
    : !readingDone
      ? (isAck ? 'Reading…' : 'Reading…')
      : isAck
        ? 'I understand — continue'
        : isLast ? 'Finish chapter' : 'Got it'

  return (
    <div
      ref={ref}
      className={`${styles.tooltip} ${isFloating ? styles.tooltipFloating : ''} ${isAck ? styles.tooltipAck : ''}`}
      style={pos ? { top: pos.top, left: pos.left } : { opacity: 0 }}
      role="dialog"
      aria-label={step.title}
    >
      <div className={styles.tooltipHead}>
        <span className={styles.chapterTag}>
          <i className={`bi ${chapter.icon}`} />
          {chapter.title}
        </span>
        <div className={styles.tooltipHeadActions}>
          <button
            className={styles.headBtn}
            onClick={onPickAnother}
            title="All chapters"
            aria-label="All chapters"
          >
            <i className="bi bi-list-ul" />
          </button>
          <button
            className={styles.headBtn}
            onClick={onPause}
            title="Close (resume later from the tour pill)"
            aria-label="Close"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
      </div>

      <div className={styles.tooltipBody}>
        {isAck && (
          <div className={styles.ackEyebrow}>
            <i className="bi bi-exclamation-triangle-fill" />
            Heads up — read this
          </div>
        )}
        <h3 className={styles.title}>{step.title}</h3>
        <p className={styles.body}>{step.body}</p>

        {hint && !observed && (
          <div className={styles.observeHint}>
            <i className="bi bi-cursor" />
            <span>{hint}</span>
          </div>
        )}

        {hint && observed && !isAck && (
          <div className={`${styles.observeHint} ${styles.observed}`}>
            <i className="bi bi-check2-circle" />
            <span>Nice. Moving on…</span>
          </div>
        )}
      </div>

      <div className={styles.tooltipFoot}>
        <span className={styles.stepCount}>
          {stepIndex + 1} / {totalSteps}
        </span>
        <div className={styles.footActions}>
          {!isAck && (
            <button className={styles.secondaryBtn} onClick={onPause}>
              Later
            </button>
          )}
          <button
            className={styles.primaryBtn}
            onClick={onGotIt}
            disabled={!readingDone}
            aria-label={buttonLabel}
          >
            {!readingDone && (
              <span
                className={styles.readingFill}
                style={{ animationDuration: `${readingMs}ms` }}
              />
            )}
            <span className={styles.primaryBtnLabel}>
              {buttonLabel}
              {readingDone && <i className={`bi ${isLast ? 'bi-check2' : 'bi-arrow-right'}`} />}
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}
